import React, { useState, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styles from "../ModernDashboard.module.css";

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

const getColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const c = (hash & 0x00ffffff).toString(16).toUpperCase();
  return "#" + "00000".substring(0, 6 - c.length) + c;
};

const formatCurrency = (val) => {
  const num = parseFloat(val);
  if (isNaN(num)) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const formatDateShort = (ds) => {
  const d = new Date(ds);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2,"0")}, ${d.getFullYear()}`;
};

const formatDateLong = (ds) => {
  const d = new Date(ds);
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const day = String(d.getDate()).padStart(2,"0");
  const hh = String(d.getHours()).padStart(2,"0");
  const mm = String(d.getMinutes()).padStart(2,"0");
  const ss = String(d.getSeconds()).padStart(2,"0");
  return `${months[d.getMonth()]} ${day}, ${d.getFullYear()} | ${hh}:${mm}:${ss}`;
};

/* ------------------------------------------------------------------ */
/*  Status Chip                                                       */
/* ------------------------------------------------------------------ */

const StatusChip = ({ paid }) => (
  <span className={paid ? styles.mdTxChipPaid : styles.mdTxChipPending}>
    <span
      className={`${styles.mdTxChipDot} ${
        paid ? styles.mdTxChipDotPaid : styles.mdTxChipDotPending
      }`}
    />
    {paid ? "Successful" : "Pending"}
  </span>
);

StatusChip.propTypes = { paid: PropTypes.bool };

/* ------------------------------------------------------------------ */
/*  Transaction Avatar                                                */
/* ------------------------------------------------------------------ */

const TxAvatar = ({ title }) => {
  const bg = useMemo(() => getColor(title), [title]);
  return (
    <div className={styles.mdTxAvatar} style={{ backgroundColor: bg }}>
      {title.charAt(0).toUpperCase()}
    </div>
  );
};

TxAvatar.propTypes = { title: PropTypes.string.isRequired };

/* ------------------------------------------------------------------ */
/*  Receipt Modal                                                     */
/* ------------------------------------------------------------------ */

const ReceiptModal = ({ tx, onClose }) => {
  const handleBackdrop = useCallback(
    (e) => { if (e.target.classList.contains("modal-backdrop")) onClose(); },
    [onClose]
  );

  if (!tx) return null;

  return (
    <div className="modal-backdrop" onClick={handleBackdrop}>
      <div className="modal-content">
        <button className="close-button" onClick={onClose}>×</button>
        <h3>Transaction Receipt</h3>
        <div className="tran-rec-unit"><span>Amount</span><h2>{formatCurrency(tx.totalFee)}</h2></div>
        <div className="tran-rec-unit"><span>Date</span><h2>{formatDateLong(tx.createdAt)}</h2></div>
        <div className="tran-rec-unit"><span>Payment Method</span><h2>{tx.paymentMethod}</h2></div>
        <div className="tran-rec-unit"><span>Description</span><h2>{tx.description}</h2></div>
        <div className="tran-rec-unit"><span>Transaction ID</span><h2>{tx.transactionId}</h2></div>
        <div className="tran-rec-unit"><span>Status</span><h2><StatusChip paid={tx.status} /></h2></div>
      </div>
    </div>
  );
};

ReceiptModal.propTypes = { tx: PropTypes.object, onClose: PropTypes.func.isRequired };

/* ------------------------------------------------------------------ */
/*  Skeleton Row                                                      */
/* ------------------------------------------------------------------ */

const SkeletonRow = () => (
  <tr>
    <td className={styles.mdTxTd}><div className={styles.mdTxTitleCell}><div className={styles.mdSkeletonCircle} /><div className={styles.mdSkeletonTextShort} /></div></td>
    <td className={styles.mdTxTd}><div className={styles.mdSkeletonTextShort} /></td>
    <td className={styles.mdTxTd}><div className={styles.mdSkeletonTextShort} /></td>
    <td className={styles.mdTxTd}><div className={styles.mdSkeletonTextShort} /></td>
    <td className={styles.mdTxTd}><div className={styles.mdSkeletonTextShort} /></td>
  </tr>
);

/* ------------------------------------------------------------------ */
/*  Main Component                                                    */
/* ------------------------------------------------------------------ */

export default function RecentTransactions({ recentTransactions = [], loading = false }) {
  const [selected, setSelected] = useState(null);
  const close = useCallback(() => setSelected(null), []);

  return (
    <>
      <div className={styles.mdTxSection}>
        <div className={styles.mdTxHeader}>
          <h3 className={styles.mdTxTitle}>Recent Transactions</h3>
          <Link to="/user/tables" className={styles.mdTxLink}>
            All Transactions →
          </Link>
        </div>

        {/* ---- Desktop Table ---- */}
        <div className={styles.mdTxCard}>
          <table className={styles.mdTxTable}>
            <thead>
              <tr>
                <th className={styles.mdTxTh}>Transaction</th>
                <th className={styles.mdTxTh}>Amount</th>
                <th className={styles.mdTxTh}>Date</th>
                <th className={styles.mdTxTh}>Status</th>
                <th className={styles.mdTxTh}>ID</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? [1, 2, 3].map((i) => <SkeletonRow key={i} />)
                : recentTransactions.map((tx) => (
                    <tr
                      key={tx.id}
                      className={styles.mdTxRow}
                      onClick={() => setSelected(tx)}
                    >
                      <td className={styles.mdTxTd}>
                        <div className={styles.mdTxTitleCell}>
                          <TxAvatar title={tx.title} />
                          <span>{tx.title}</span>
                        </div>
                      </td>
                      <td className={styles.mdTxTd}>{formatCurrency(tx.totalFee)}</td>
                      <td className={styles.mdTxTd}>{formatDateShort(tx.createdAt)}</td>
                      <td className={styles.mdTxTd}><StatusChip paid={tx.status} /></td>
                      <td className={styles.mdTxTd} style={{ fontFamily: "monospace", fontSize: "0.7rem" }}>
                        {tx.transactionId}
                      </td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>

        {/* ---- Mobile Card List ---- */}
        <div className={styles.mdTxCardList}>
          {loading
            ? [1, 2, 3].map((i) => (
                <div key={i} className={styles.mdTxMobileCard}>
                  <div className={styles.mdSkeletonTextLong} />
                  <div className={styles.mdSkeletonTextShort} />
                </div>
              ))
            : recentTransactions.map((tx) => (
                <div
                  key={tx.id}
                  className={styles.mdTxMobileCard}
                  onClick={() => setSelected(tx)}
                >
                  <div className={styles.mdTxMobileRow}>
                    <div className={styles.mdTxTitleCell}>
                      <TxAvatar title={tx.title} />
                      <strong>{tx.title}</strong>
                    </div>
                    <StatusChip paid={tx.status} />
                  </div>
                  <div className={styles.mdTxMobileRow}>
                    <span className={styles.mdTxMobileLabel}>Amount</span>
                    <span>{formatCurrency(tx.totalFee)}</span>
                  </div>
                  <div className={styles.mdTxMobileRow}>
                    <span className={styles.mdTxMobileLabel}>Date</span>
                    <span>{formatDateShort(tx.createdAt)}</span>
                  </div>
                  <div className={styles.mdTxMobileRow}>
                    <span className={styles.mdTxMobileLabel}>ID</span>
                    <span style={{ fontFamily: "monospace", fontSize: "0.65rem" }}>
                      {tx.transactionId}
                    </span>
                  </div>
                </div>
              ))}
        </div>
      </div>

      {/* Receipt Modal */}
      {selected && <ReceiptModal tx={selected} onClose={close} />}
    </>
  );
}

RecentTransactions.propTypes = {
  recentTransactions: PropTypes.array,
  loading: PropTypes.bool,
};